import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch, API_BASE } from "../api";

// Poll interval for the Apps list. Older code used 5s which caused ~N+1 requests
// every 5 seconds (one list + one manifest per app) — serious backend thrash
// once a user had ~30 apps. We now (a) bump this to 30s, and (b) fold version
// info into the /integrations response itself (backend change), collapsing the
// per-cycle cost to a single HTTP call.
const POLL_INTERVAL_MS = 30000;
const PAGE_SIZE = 100;

export default function useIntegrations(enabled = true) {
  const [integrations, setIntegrations] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const visibleRef = useRef(true);

  const fetchIntegrations = useCallback(async () => {
    if (!visibleRef.current) return;
    try {
      // /api/integrations returns version/publishedAt inline, so no per-app
      // manifest fan-out is needed — but it paginates server-side (default
      // limit=20), so limit/offset must be passed explicitly.
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      const res = await authFetch(`${API_BASE}/integrations?${params}`);
      const data = await res.json();
      setIntegrations(data.integrations || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch integrations:", err);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    if (!enabled) return;
    fetchIntegrations();
    const interval = setInterval(fetchIntegrations, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
      if (visibleRef.current) fetchIntegrations();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, fetchIntegrations]);

  // id is the app's real id (apps.id) — the backend's resolveAppId
  // middleware also accepts a legacy display name for any caller not yet
  // updated, but every call site in this app now passes id.
  const doAction = useCallback(async (id, action, method = "POST") => {
    setActionLoading(`${id}-${action}`);
    try {
      const url =
        action === "remove"
          ? `${API_BASE}/integrations/${id}`
          : `${API_BASE}/integrations/${id}/${action}`;
      await authFetch(url, { method });
      await fetchIntegrations();
    } catch (err) {
      console.error(`${action} failed:`, err);
    } finally {
      setActionLoading(null);
    }
  }, [fetchIntegrations]);

  return { integrations, total, offset, setOffset, loading, actionLoading, doAction, fetchIntegrations };
}
